import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { createLazyLoadTrigger } from '../lib/lazyLoad';

const CALCOM_SCRIPT_SRC = 'https://app.cal.com/embed/embed.js';
const CALCOM_ORIGIN = 'https://app.cal.com';
const DEFAULT_LAYOUT = 'month_view';
const DEFAULT_THEME = 'light';
const DEFAULT_BRAND_COLOR = '#ff3000';
const DEFAULT_CAL_LINK = 'yuri-kovalov-vbkn2j/30min';

let calcomScriptPromise = null;
let widgetIdCounter = 0;

function queueCalCall(api, args) {
  api.q.push(args);
}

function installCalcomQueue() {
  if (typeof window.Cal === 'function' && window.Cal.ns) return;

  window.Cal =
    window.Cal ||
    function () {
      const cal = window.Cal;
      const args = arguments;

      cal.ns = cal.ns || {};
      cal.q = cal.q || [];

      if (args[0] === 'init') {
        const api = function () {
          queueCalCall(api, arguments);
        };
        const namespace = args[1];

        api.q = api.q || [];

        if (typeof namespace === 'string') {
          cal.ns[namespace] = cal.ns[namespace] || api;
          queueCalCall(cal.ns[namespace], args);
          queueCalCall(cal, ['initNamespace', namespace]);
        } else {
          queueCalCall(cal, args);
        }

        return;
      }

      queueCalCall(cal, args);
    };

  window.Cal.ns = window.Cal.ns || {};
  window.Cal.q = window.Cal.q || [];
}

function getExistingCalcomScript() {
  return document.querySelector(
    `script[src="${CALCOM_SCRIPT_SRC}"], script[src*="app.cal.com/embed/embed.js"]`,
  );
}

function loadCalcomScript() {
  installCalcomQueue();

  const existingScript = getExistingCalcomScript();

  if (existingScript) {
    return calcomScriptPromise || Promise.resolve();
  }

  if (!calcomScriptPromise) {
    calcomScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = CALCOM_SCRIPT_SRC;
      script.async = true;

      script.onload = () => resolve();
      script.onerror = (err) => {
        calcomScriptPromise = null;
        reject(err);
      };

      window.Cal.loaded = true;
      document.head.appendChild(script);
    });
  }

  return calcomScriptPromise;
}

function getBooleanDataValue(value, fallback = true) {
  if (value === 'false') return false;
  if (value === 'true') return true;

  return fallback;
}

function getWidgetSelector(widget) {
  const customId = widget.dataset.calcomId;

  if (customId && !widget.id) {
    widget.id = customId;
  }

  if (!widget.id) {
    widgetIdCounter += 1;
    widget.id = `calcom-inline-widget-${widgetIdCounter}`;
  }

  return `#${widget.id}`;
}

function initCalcomWidget(widget) {
  if (!widget || widget.dataset.calcomInitialized === 'true') {
    return Promise.resolve();
  }

  const namespace = widget.dataset.calcomNamespace || '30min';
  const calLink = widget.dataset.calcomLink || DEFAULT_CAL_LINK;

  widget.dataset.calcomInitialized = 'true';

  const layout = widget.dataset.calcomLayout || DEFAULT_LAYOUT;
  const theme = widget.dataset.calcomTheme || DEFAULT_THEME;
  const brandColor = widget.dataset.calcomBrandColor || DEFAULT_BRAND_COLOR;
  const hideEventTypeDetails = getBooleanDataValue(
    widget.dataset.calcomHideEventTypeDetails,
  );
  const useSlotsViewOnSmallScreen =
    widget.dataset.calcomUseSlotsViewOnSmallScreen || 'true';

  const scriptPromise = loadCalcomScript();

  window.Cal('init', namespace, { origin: CALCOM_ORIGIN });

  window.Cal.ns[namespace]('inline', {
    elementOrSelector: getWidgetSelector(widget),
    config: {
      layout,
      useSlotsViewOnSmallScreen,
      theme,
    },
    calLink,
  });

  window.Cal.ns[namespace]('ui', {
    hideEventTypeDetails,
    layout,
    cssVarsPerTheme: {
      light: { 'cal-brand': brandColor },
      dark: { 'cal-brand': brandColor },
    },
  });

  return scriptPromise;
}

export function initAllCalcomWidgets({
  selector = '.js-calcom-inline-widget',
  start = 'top bottom+=100%',
} = {}) {
  const widgets = document.querySelectorAll(selector);

  if (!widgets.length) return;

  widgets.forEach((widget) => {
    if (widget.dataset.calcomLazyInit === 'true') return;
    widget.dataset.calcomLazyInit = 'true';

    createLazyLoadTrigger({
      trigger: widget,
      start,
      onLoad: () =>
        initCalcomWidget(widget).then(() => {
          ScrollTrigger.refresh();
        }),
      onError: (err) => {
        widget.dataset.calcomInitialized = 'false';
        console.error('Failed to load Cal.com script:', err);
      },
    });
  });
}
